import React, { useEffect, useState } from "react";
import useLocalStorage from "../lib/UseLocalStorage";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "../components/Configurator/ui/Form";

import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { z } from "zod";
import { Input } from "../components/Configurator/ui/Input";
import { buttonVariants } from "../components/Configurator/ui/Button";
import {useAppContext} from "../context/AppContext";
import SummaryList from "./summary/SummaryList";
import DoorView from "../components/Configurator/scene/DoorView";
import ConfiguredDoor from "../components/Configurator/scene/ConfiguredDoor";
import {toast} from "sonner";
import { basketEcommerceParams, trackEventBeforeNavigate } from "../lib/analytics";

const formSchema = z.object({
    shipping_address_line_1: z.string().nonempty({ message: "Address 1 is required" }),
    shipping_address_line_2: z.string().optional(),
    shipping_address_line_3: z.string().optional(),
    shipping_city: z.string().nonempty({ message: "City is required" }),
    shipping_county: z.string().nonempty({ message: "County is required" }),
    shipping_postcode: z.string().nonempty({ message: "Postcode is required" }),
});

const formDefaultValues = {
    shipping_address_line_1: "",
    shipping_address_line_2: "",
    shipping_address_line_3: "",
    shipping_city: "",
    shipping_county: "",
    shipping_postcode: "",
};

const Basket = ({ type }) => {
    const [basket, setBasket] = useState();
    const [liveShippingPence, setLiveShippingPence] = useState(null);
    const [shippingLoading, setShippingLoading] = useState(false);
    const [doorFullscreen, setDoorFullscreen] = useState(false);
    const { getValue, setValue } = useLocalStorage();
    const { csrfToken, setCurrentPage } = useAppContext();
    const customerStripeEnabled = Boolean(window.initialData?.customer_stripe_enabled);

    // Lock body scroll when fullscreen door is open
    useEffect(() => {
        if (typeof document === "undefined") return;
        if (doorFullscreen) {
            const prevOverflow = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            return () => { document.body.style.overflow = prevOverflow; };
        }
    }, [doorFullscreen]);

    // ESC key closes fullscreen
    useEffect(() => {
        if (!doorFullscreen) return;
        const handler = (e) => {
            if (e.key === "Escape") setDoorFullscreen(false);
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [doorFullscreen]);

    const fullUrl = window.location.href; // Get the full URL
    const beforeQuestionMark = fullUrl.split('?')[0]; // Get the part before the question mark
    const urlParts = beforeQuestionMark.split('/'); // Split the URL by '/'
    const key = urlParts[urlParts.length - 1];
    const showProceedToPayment = type === "trade" && !!basket?.installer?.pay_on_account;
    const formatGBPFromPence = (amountPence) => {
        const amountNumber = Number(amountPence ?? 0);
        const safePence = Number.isFinite(amountNumber) ? amountNumber : 0;
        return new Intl.NumberFormat("en-GB", {
            style: "currency",
            currency: "GBP",
        }).format(safePence / 100);
    };
    const subtotalPence = basket ? Number(basket?.total_amount ?? 0) : 0;
    const shippingPence = liveShippingPence !== null ? liveShippingPence : (basket ? Number(basket?.shipping_amount ?? 0) : 0);
    const totalPence = subtotalPence + shippingPence;

    useEffect(() => {
        const getBasket = async (id) => {
            try {
                const res = await axios.get(`/basket/${id}`);
                setBasket(res?.data?.basket);
                console.log(res?.data?.basket);
            } catch (error) {
                console.log(error);
            }
        };

        const id = getValue("basketId");
        if (id) {
            getBasket(id);
        }
    }, []);

    const methods = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: formDefaultValues,
    });

    useEffect(() => {
        if (!basket) {
            return;
        }

        const savedShippingDetails = getValue("shippingDetails");

        methods.reset({
            shipping_address_line_1:
                savedShippingDetails?.shipping_address_line_1 ??
                basket?.shipping_address_line_1 ??
                "",
            shipping_address_line_2:
                savedShippingDetails?.shipping_address_line_2 ??
                basket?.shipping_address_line_2 ??
                "",
            shipping_address_line_3:
                savedShippingDetails?.shipping_address_line_3 ??
                basket?.shipping_address_line_3 ??
                "",
            shipping_city:
                savedShippingDetails?.shipping_city ??
                basket?.shipping_city ??
                "",
            shipping_county:
                savedShippingDetails?.shipping_county ??
                basket?.shipping_county ??
                "",
            shipping_postcode:
                savedShippingDetails?.shipping_postcode ??
                basket?.shipping_postcode ??
                basket?.customer?.postcode ??
                "",
        });
    }, [basket, getValue, methods.reset]);

    const {
        control,
        formState: { errors },
        watch,
    } = methods;

    const shippingPostcode = watch("shipping_postcode");

    // Live shipping calculation effect
    useEffect(() => {
        if (!basket || !shippingPostcode) {
            setLiveShippingPence(null);
            return;
        }

        const postcodeValue = String(shippingPostcode ?? "")
            .trim()
            .toUpperCase()
            .replace(/\s+/g, "");

        if (postcodeValue.length < 5) {
            setLiveShippingPence(null);
            return;
        }

        let active = true;
        const timeoutId = setTimeout(async () => {
            try {
                setShippingLoading(true);

                const res = await axios.post(`/basket/${basket.id}/shipping-estimate`, {
                    postcode: postcodeValue,
                    _token: csrfToken,
                });

                if (!active) {
                    return;
                }

                const nextShipping = res?.data?.shipping_amount;
                const shippingNumber = nextShipping == null ? 0 : Number(nextShipping);
                setLiveShippingPence(Number.isFinite(shippingNumber) ? shippingNumber : 0);
                setShippingLoading(false);
            } catch (e) {
                if (!active) {
                    return;
                }
                setShippingLoading(false);
                setLiveShippingPence(0);
                console.error('Shipping estimate error:', e);
            }
        }, 450);

        return () => {
            active = false;
            clearTimeout(timeoutId);
        };
    }, [basket, shippingPostcode, csrfToken]);

    const updateShipping = async (data) => {
        const res = await axios.patch(`/basket/${basket?.id}/shipping`, {
            ...data,
            _token: csrfToken,
        });
        setValue("shippingDetails", data);
        setBasket(res?.data?.basket);
        return res?.data?.basket;
    };

    const handleStripePayment = async (data) => {
        if (!basket) {
            toast.error(`Basket is Empty`, { duration: 5000 });
            return;
        }
        try {
            const updatedBasket = await updateShipping(data);
            const updatedSubtotalPence = Number(updatedBasket?.total_amount ?? 0);
            const updatedShippingPence = Number(updatedBasket?.shipping_amount ?? 0);
            const updatedTotalPence = updatedSubtotalPence + updatedShippingPence;
            const payment = await axios.post(`/payment`, {
                external_door_style: updatedBasket.basket_items[0]?.external_door_style,
                total_amount: updatedTotalPence,
                basket_id: updatedBasket.id,
                config_key: key,
                checkout_type: type,
                _token: csrfToken,
            });
            await trackEventBeforeNavigate("add_payment_info", {
                ...basketEcommerceParams(updatedBasket, type),
                payment_type: "card",
            });
            window.top.location.href = payment?.data?.redirect_url;
        } catch (e) {
            const message = e?.response?.data?.message || 'Something went wrong. Please try again.';
            toast.error(message, { duration: 5000 });
        }
    };

    const handleAddToAccount = async (data) => {
        if (!basket) {
            toast.error(`Basket is Empty`, { duration: 5000 });
            return;
        }
        try {
            const updatedBasket = await updateShipping(data);
            const res = await axios.post(`/payment/account`, {
                basket_id: updatedBasket.id,
                config_key: key,
                checkout_type: type,
                _token: csrfToken,
            });
            await trackEventBeforeNavigate("add_payment_info", {
                ...basketEcommerceParams(updatedBasket, type),
                payment_type: "account",
            });
            window.top.location.href = res?.data?.redirect_url;
        } catch (e) {
            const message = e?.response?.data?.message || 'Something went wrong. Please try again.';
            toast.error(message, { duration: 5000 });
        }
    };

    return (
        <>
        <div className="grid grid-cols-1 lg:grid-cols-5 p-4 sm:p-8 lg:p-12 gap-4 lg:gap-6">
            <FormProvider {...methods}>
                <form className="flex flex-col h-full order-2 lg:order-1 lg:col-span-2">
                    <div className="flex flex-col gap-4 bg-white w-full p-4 sm:p-8 shadow-md">
                        <h3 className="text-primary uppercase text-xl">
                            Shipping Details
                        </h3>
                        <div className="bg-gray-200 w-full h-[1px]"></div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 p-3 sm:p-4 bg-[#F6F4F3]">
                            <div className="flex flex-col gap-1 sm:gap-2">
                                <p className="text-gray-500 text-xs sm:text-base">Subtotal (Net)</p>
                                <p className="text-gray-700 font-semibold text-sm sm:text-base">
                                    {formatGBPFromPence(subtotalPence)}
                                </p>
                            </div>
                            <div className="flex flex-col gap-1 sm:gap-2">
                                <p className="text-gray-500 text-xs sm:text-base">VAT (20%)</p>
                                <p className="text-gray-700 font-semibold text-sm sm:text-base">
                                    {formatGBPFromPence(Math.round(subtotalPence * 0.2))}
                                </p>
                            </div>
                            <div className="flex flex-col gap-1 sm:gap-2">
                                <p className="text-gray-500 text-xs sm:text-base">Shipping</p>
                                <p className="text-gray-700 font-semibold text-sm sm:text-base">
                                    {shippingLoading ? "Calculating..." : formatGBPFromPence(shippingPence)}
                                </p>
                            </div>
                            <div className="flex flex-col gap-1 sm:gap-2">
                                <p className="text-gray-500 text-xs sm:text-base">Total (incl. VAT)</p>
                                <p className="text-gray-700 font-semibold text-sm sm:text-base">
                                    {/* Shipping VAT removed — was: Math.round(shippingPence * 1.2) instead of shippingPence. Restore if shipping VAT is needed again. */}
                                    {formatGBPFromPence(Math.round(subtotalPence * 1.2) + shippingPence)}
                                </p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 p-3 sm:p-4 gap-3 sm:gap-0 bg-[#F6F4F3]">
                            <div className="flex flex-col gap-1 sm:gap-2">
                                <p className="text-gray-500 text-xs sm:text-base">Customer Name</p>
                                <p className="text-gray-700 text-sm sm:text-base">
                                    {basket?.customer?.first_name}
                                </p>
                            </div>
                            <div className="flex flex-col gap-1 sm:gap-2">
                                <p className="text-gray-500 text-xs sm:text-base">Email Address</p>
                                <p className="text-gray-700 text-sm sm:text-base break-all sm:break-normal">
                                    {basket?.customer?.email}
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col gap-4">
                            <FormField
                                control={control}
                                name="shipping_address_line_1"
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel className="text-gray-700">
                                            House Number
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                type="text"
                                                className="text-gray-700"
                                                placeholder="Enter Your Address 1"
                                                {...field}
                                            />
                                        </FormControl>
                                        {errors.shipping_address_line_1 && (
                                            <FormMessage>
                                                {errors.shipping_address_line_1.message}
                                            </FormMessage>
                                        )}
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={control}
                                name="shipping_address_line_2"
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel className="text-gray-700">
                                            Address Line 1
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                type="text"
                                                className="text-gray-700"
                                                placeholder="Enter Your Address Line 1"
                                                {...field}
                                            />
                                        </FormControl>
                                        {errors.shipping_address_line_2 && (
                                            <FormMessage>
                                                {errors.shipping_address_line_2.message}
                                            </FormMessage>
                                        )}
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={control}
                                name="shipping_address_line_3"
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel className="text-gray-700">
                                            Address Line 2
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                type="text"
                                                className="text-gray-700"
                                                placeholder="Enter Your Address Line 2"
                                                {...field}
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={control}
                                className="w-1/2"
                                name="shipping_city"
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel className="text-gray-700">
                                            City
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                type="text"
                                                className="text-gray-700"
                                                placeholder="Enter Your City"
                                                {...field}
                                            />
                                        </FormControl>
                                        {errors.shipping_city && (
                                            <FormMessage>
                                                {errors.shipping_city.message}
                                            </FormMessage>
                                        )}
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={control}
                                className="w-1/2"
                                name="shipping_county"
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel className="text-gray-700">
                                            County
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                type="text"
                                                className="text-gray-700"
                                                placeholder="Enter Your County"
                                                {...field}
                                            />
                                        </FormControl>
                                        {errors.shipping_county && (
                                            <FormMessage>
                                                {errors.shipping_county.message}
                                            </FormMessage>
                                        )}
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={control}
                                name="shipping_postcode"
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel className="text-gray-700">
                                            Postcode
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                type="text"
                                                className="text-gray-700"
                                                placeholder="Enter Your Postcode"
                                                {...field}
                                            />
                                        </FormControl>
                                        {errors.shipping_postcode && (
                                            <FormMessage>
                                                {errors.shipping_postcode.message}
                                            </FormMessage>
                                        )}
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className="flex flex-wrap justify-end gap-3 sm:gap-4">
                            <button
                                type="button"
                                onClick={() => setCurrentPage('home')}
                                className={buttonVariants({
                                    className:
                                        "flex items-center justify-between text-center gap-1 w-fit uppercase self-end !bg-black",
                                })}
                            >
                                Back
                            </button>

                            {type === "trade" ? (
                                <>
                                    {basket?.installer?.pay_on_account && (
                                        // Only shown when pay_on_account is enabled
                                        <button
                                            type="button"
                                            onClick={methods.handleSubmit(handleAddToAccount)}
                                            className={buttonVariants({
                                                className:
                                                    "flex items-center justify-between text-center gap-1 w-fit uppercase self-end !bg-black",
                                            })}
                                        >
                                            Add to Account
                                        </button>
                                    )}
                                    {/* // Always shown for trade */}
                                    <button
                                        type="button"
                                        onClick={methods.handleSubmit(handleStripePayment)}
                                        className={buttonVariants({
                                            className:
                                                "flex items-center justify-between text-center gap-1 w-fit uppercase self-end",
                                        })}
                                    >
                                        Proceed to Payment
                                    </button>
                                </>
                            ) : (
                                customerStripeEnabled ? (
                                    <button
                                        type="button"
                                        onClick={methods.handleSubmit(handleStripePayment)}
                                        className={buttonVariants({
                                            className:
                                                "flex items-center justify-between text-center gap-1 w-fit uppercase self-end",
                                        })}
                                    >
                                        Place The Order
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => setCurrentPage("home")}
                                        className={buttonVariants({
                                            className:
                                                "flex items-center justify-between text-center gap-1 w-fit uppercase self-end !bg-black",
                                        })}
                                    >
                                        Back to Configurator
                                    </button>
                                )
                            )}
                        </div>
                    </div>
                </form>
            </FormProvider>

            <div className="flex flex-col gap-4 w-full h-full bg-[#424242] order-1 lg:order-2 lg:col-span-3">
                <h3 className="text-white/80 uppercase text-lg p-4 sm:p-6">Order Summary</h3>
                <div className="bg-gray-200 w-full h-[1px]"></div>
                {basket ? (<div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6 p-4 md:p-6 items-start h-full min-h-0">
                    {/* Door preview — now takes 3/5 columns + taller height + fullscreen toggle */}
                    <div className="relative w-full lg:col-span-2 flex flex-col min-h-[60vh] lg:min-h-0 lg:h-[70vh] xl:h-[75vh] rounded-xl overflow-hidden shadow-2xl bg-[#eeecea] ring-1 ring-white/10">
                        <DoorView
                            onToggleFullscreen={() => setDoorFullscreen(true)}
                            isFullscreen={false}
                        />
                        <svg
                            className="bg-[#eeecea] w-full h-full flex-1 object-contain"
                            xmlns="http://www.w3.org/2000/svg"
                            xmlnsXlink="http://www.w3.org/1999/xlink"
                            viewBox="900 150 2000 2500"
                            preserveAspectRatio="xMidYMid meet"
                        >
                            <ConfiguredDoor translate='translate(1200,375)'/>
                        </svg>
                        {/* Fullscreen hint bar */}
                        {/* <div className="absolute left-1/2 -translate-x-1/2 bottom-2 sm:bottom-3 px-3 py-1 bg-black/40 text-white/90 text-[10px] sm:text-xs rounded-full backdrop-blur-sm pointer-events-none">
                            Click the ⤢ icon for full screen
                        </div> */}
                    </div>
                    <div className="flex flex-col lg:col-span-3 min-h-0 overflow-y-auto">
                        {/* Pricing Section */}
                        <div className="bg-white/5 rounded-lg p-4 mb-6 hidden">
                            <h4 className="text-white/90 text-lg font-semibold mb-4 uppercase">Pricing</h4>
                            <div className="flex justify-between gap-2 py-2">
                                <p className="text-white text-sm">Price</p>
                                <p className="text-white text-sm">
                                    {formatGBPFromPence(basket?.total_amount)}
                                </p>
                            </div>
                            <div className="flex justify-between gap-2 py-2">
                                <p className="text-white text-sm">Shipping</p>
                                <p className="text-white text-sm">
                                    {formatGBPFromPence(basket?.shipping_amount)}
                                </p>
                            </div>
                            <div className="bg-gray-100 opacity-50 w-full h-[1px] my-2"></div>
                            <div className="flex justify-between gap-2 py-2">
                                <p className="text-white text-sm font-semibold">Total</p>
                                <p className="text-white text-sm font-semibold">
                                    {formatGBPFromPence(totalPence)}
                                </p>
                            </div>
                        </div>

                        {/* Configuration Details Section */}
                        <div className="bg-white/5 rounded-lg p-4">
                            <SummaryList doorPricePence={subtotalPence} type={type} basketItem={basket?.basket_items?.[0] ?? null}/>
                        </div>
                    </div>
                </div>):(<p>Basket is Empty</p>)}
            </div>
        </div>

        {/* ──────────────────────────────────────────────────
            FULLSCREEN DOOR OVERLAY (true full viewport size)
           ────────────────────────────────────────────────── */}
        {doorFullscreen && basket && (
            <div className="fixed inset-0 z-[9999] bg-black/95 flex flex-col animate-in fade-in duration-200">
                {/* Top bar: DoorView controls + close button */}
                <div className="relative w-full flex items-start justify-between p-4 sm:p-6 flex-shrink-0">
                    <div className="flex flex-col gap-2">
                        <h2 className="text-white/90 uppercase text-xl sm:text-2xl font-semibold tracking-wide">
                            {String(basket?.basket_items?.[0]?.external_door_style ?? "Door").toUpperCase()} — Full Size Preview
                        </h2>
                        <p className="text-white/50 text-xs sm:text-sm">
                            Press <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white/70 border border-white/20">Esc</kbd> or click ✕ to close
                        </p>
                    </div>
                    <div className="flex items-start gap-3">
                        <DoorView
                            onToggleFullscreen={() => setDoorFullscreen(false)}
                            isFullscreen={true}
                        />
                        <button
                            type="button"
                            onClick={() => setDoorFullscreen(false)}
                            aria-label="Close fullscreen"
                            className="p-3 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors ring-1 ring-white/10"
                        >
                            <FontAwesomeIcon icon={faXmark} className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                {/* Main door stage — fills ALL remaining viewport */}
                <div className="relative flex-1 min-h-0 w-full flex items-center justify-center p-2 sm:p-8">
                    <div className="relative w-full h-full max-w-[90vw] max-h-[82vh] rounded-2xl overflow-hidden bg-[#eeecea] shadow-[0_0_120px_rgba(204,33,7,0.25)] ring-1 ring-white/5">
                        <svg
                            className="bg-[#eeecea] w-full h-full block"
                            xmlns="http://www.w3.org/2000/svg"
                            xmlnsXlink="http://www.w3.org/1999/xlink"
                            viewBox="900 150 2000 2500"
                            preserveAspectRatio="xMidYMid meet"
                        >
                            <ConfiguredDoor translate='translate(1200,375)'/>
                        </svg>
                    </div>
                </div>

                {/* Bottom dimension bar (optional, handy context) */}
                {basket?.basket_items?.[0] && (
                    <div className="flex-shrink-0 flex flex-wrap items-center justify-center gap-3 sm:gap-6 p-3 sm:p-5 text-white/70 text-xs sm:text-sm border-t border-white/5">
                        {basket.basket_items[0].width && basket.basket_items[0].height && (
                            <span className="inline-flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-[#cc2107]"></span>
                                Door: {basket.basket_items[0].width} × {basket.basket_items[0].height} mm
                            </span>
                        )}
                        {basket.basket_items[0].aperture_width && basket.basket_items[0].aperture_height && (
                            <span className="inline-flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-white/40"></span>
                                Aperture: {basket.basket_items[0].aperture_width} × {basket.basket_items[0].aperture_height} mm
                            </span>
                        )}
                        {basket.basket_items[0].opening_direction && (
                            <span className="inline-flex items-center gap-2">
                                Hung: {String(basket.basket_items[0].opening_direction).toUpperCase()}
                            </span>
                        )}
                    </div>
                )}
            </div>
        )}
        </>
    );
};

export default Basket;
